/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Hook for bulk operation undo management
 */

"use client";

import { logger } from "@/lib/utils/logger";
import { useCallback, useMemo, useState } from "react";
import type { BulkOperationMode, UndoState } from "./types";

interface UseBulkUndoOptions<T> {
  maxUndoHistory: number;
  defaultUndoTimeLimit: number;
  onUndo?: (undoState: UndoState<T>) => Promise<void>;
}

/**
 * Hook for managing bulk operation undo functionality
 */
export function useBulkUndo<T>({
  maxUndoHistory,
  defaultUndoTimeLimit,
  onUndo,
}: UseBulkUndoOptions<T>) {
  // Undo history state
  const [undoHistory, setUndoHistory] = useState<UndoState<T>[]>([]);

  // Add undo operation to history
  const addUndoOperation = useCallback(
    (
      operationId: string,
      type: BulkOperationMode,
      affectedItems: T[],
      undoTimeLimit?: number,
    ) => {
      const now = new Date();
      const expiresAt = new Date(
        now.getTime() + (undoTimeLimit ?? defaultUndoTimeLimit) * 1000,
      );

      const undoState: UndoState<T> = {
        operationId,
        type,
        affectedItems: [...affectedItems],
        timestamp: now,
        expiresAt,
        canUndo: true,
      };

      setUndoHistory((prev) => {
        const updated = [undoState, ...prev.slice(0, maxUndoHistory - 1)];

        // Schedule cleanup of expired undo states
        setTimeout(
          () => {
            setUndoHistory((current) =>
              current.filter((state) => state.expiresAt.getTime() > Date.now()),
            );
          },
          (undoTimeLimit ?? defaultUndoTimeLimit) * 1000,
        );

        return updated;
      });
    },
    [maxUndoHistory, defaultUndoTimeLimit],
  );

  // Get current undo state
  const currentUndo: UndoState<T> | null =
    undoHistory.length > 0 ? undoHistory[0]! : null;
  const canUndo = useMemo(
    () => currentUndo !== null && currentUndo.expiresAt.getTime() > Date.now(),
    [currentUndo],
  );

  // Undo operation
  const undoOperation = useCallback(
    async (operationId: string) => {
      const undoState = undoHistory.find(
        (state) => state.operationId === operationId,
      );
      if (
        !undoState ||
        !undoState.canUndo ||
        undoState.expiresAt.getTime() <= Date.now()
      ) {
        return;
      }

      try {
        await onUndo?.(undoState);

        // Remove from undo history
        setUndoHistory((prev) =>
          prev.filter((state) => state.operationId !== operationId),
        );
      } catch (error) {
        logger.error("Undo operation failed", {
          error: error instanceof Error ? error.message : error,
        });
        throw error;
      }
    },
    [undoHistory, onUndo],
  );

  // Undo last operation
  const undoLastOperation = useCallback(async () => {
    if (currentUndo) {
      await undoOperation(currentUndo.operationId);
    }
  }, [currentUndo, undoOperation]);

  // Clear undo history
  const clearUndoHistory = useCallback(() => {
    setUndoHistory([]);
  }, []);

  return {
    undoHistory,
    currentUndo,
    canUndo,
    addUndoOperation,
    undoOperation,
    undoLastOperation,
    clearUndoHistory,
  };
}
